import React, { useState } from 'react';
import { uniqBy } from 'lodash';

import { Heading } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import PlusIcon from '~/components/icons/PlusIcon';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  MoiMethodInfoListMoiMethodInfo,
  MoiVendorAssignmentRuleCreateParams,
  MoiVendorAssignmentRuleInfo,
} from '~/server_shared/openapi/data-contracts';

import {
  createMoiAssignmentRule,
  deleteMoiAssignmentRule,
  moiAssignmentRulesUrl,
  reorderMoiAssignmentRules,
  setMoiAssignmentRuleEnabled,
  updateMoiAssignmentRule,
} from './api/moiAssignmentRulesApi';
import MoiAssignmentRulesTable from './components/MoiAssignmentRulesTable';
import { useContactNames } from './hooks/useContactNames';
import { useOrganizationMoiMethods } from './hooks/useOrganizationMoiMethods';
import AddOrEditMoiAssignmentRuleDialog from './AddOrEditMoiAssignmentRuleDialog';
import { TEXTS } from './texts';

/** One name per method key, the one the rule dialog offers for it: the organization's methods are
 *  configured per LOB, so a key offered on several LOBs comes back once for each, and the dialog
 *  keeps the first of them. */
const toMoiMethodNameByKey = (methods: MoiMethodInfoListMoiMethodInfo[]): Record<string, string> =>
  Object.fromEntries(uniqBy(methods, (method) => method.key).map((method) => [method.key, method.display_name]));

const staticContactIdsOf = (rules: MoiVendorAssignmentRuleInfo[]): number[] =>
  rules.flatMap((rule) => (rule.assignee.strategy === 'static' ? [rule.assignee.contact_id] : []));

/** The name the tab resolved for a static rule's contact, which the dialog's picker shows until it
 *  has read the contact itself; `undefined` for a role rule, and for a name still loading or not
 *  found. */
const staticContactNameOf = (
  rule: MoiVendorAssignmentRuleInfo | null,
  contactNamesById: Record<number, string>
): string | undefined =>
  rule?.assignee.strategy === 'static' ? contactNamesById[rule.assignee.contact_id] : undefined;

const MoiAssignmentRulesTab: React.FC = () => {
  // `useSysconfig` is exported from a JSX file without types; cast at the boundary.
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<MoiVendorAssignmentRuleInfo | null>(null);

  const {
    isLoading: isLoadingRules,
    isError: isErrorRules,
    data: rules,
    reloadData: reloadRules,
  } = useDataFetcher<MoiVendorAssignmentRuleInfo[]>(moiAssignmentRulesUrl(organization.id));
  const { methods, isLoading: isLoadingMethods } = useOrganizationMoiMethods(organization.id);

  // Local order so a drop reorders instantly; resynced whenever the fetched data changes, which
  // also reverts a failed reorder. The resync happens during render (not in an effect) so the new
  // order is reflected in the same paint — otherwise the table's empty-state gate would read a
  // stale empty `orderedRules` for one post-load paint and briefly flash the empty state over an
  // organization that has rules. Same shape as the Fund Accounts sibling.
  const [orderedRules, setOrderedRules] = useState<MoiVendorAssignmentRuleInfo[]>(rules ?? []);
  const [syncedRules, setSyncedRules] = useState(rules);
  if (syncedRules !== rules) {
    setSyncedRules(rules);
    setOrderedRules(rules ?? []);
  }

  const { namesById: contactNamesById } = useContactNames(organization.id, staticContactIdsOf(orderedRules));

  const withProcessing = async (fn: () => Promise<void>) => {
    setIsProcessing(true);
    try {
      await fn();
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitRule = async (values: MoiVendorAssignmentRuleCreateParams) => {
    await withProcessing(async () => {
      if (ruleToEdit) {
        await updateMoiAssignmentRule(organization.id, ruleToEdit.id, values);
      } else {
        await createMoiAssignmentRule(organization.id, values);
      }
    });
  };

  // Delete and enable are fired from the row, and the switch drops the promise it is handed, so a
  // rethrow from `withProcessing` would surface there as an unhandled rejection: both swallow the
  // failure once the api layer has reported it, as the Fund Accounts screen does. Create and update
  // keep rethrowing, because the dialog stays open on a failed save only when its submit rejects.
  const handleDeleteRule = async (rule: MoiVendorAssignmentRuleInfo) => {
    try {
      await withProcessing(() => deleteMoiAssignmentRule(organization.id, rule.id));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEnabledChanged = async (rule: MoiVendorAssignmentRuleInfo) => {
    try {
      await withProcessing(() => setMoiAssignmentRuleEnabled(organization.id, rule.id, !rule.is_enabled));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  // The table lists every rule, unfiltered, so the positions the list reports are positions in
  // `orderedRules`. The payload is the whole live order, not a delta, because the endpoint rejects
  // anything that is not an exact permutation of every live rule.
  const handleReorder = async ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    const reordered = [...orderedRules];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setOrderedRules(reordered);

    setIsProcessing(true);
    try {
      await reorderMoiAssignmentRules(
        organization.id,
        reordered.map((rule) => rule.id)
      );
      await reloadRules();
    } catch {
      // The BE checks the whole order before it moves any rule and rejects it as one, so there is
      // no partial state to keep — revert to the server order.
      setOrderedRules(rules ?? []);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditRuleClicked = (rule: MoiVendorAssignmentRuleInfo) => {
    setRuleToEdit(rule);
    setIsRuleDialogOpen(true);
  };

  const handleCloseRuleDialog = () => {
    setIsRuleDialogOpen(false);
    setRuleToEdit(null);
  };

  // Only the rules' own read can fail the tab. The methods merely name the MOI Methods cells, and
  // their route asks for claim-configuration access that an operator allowed to edit these rules
  // may lack; without them each method shows as its key. Their load is still waited for, so those
  // keys do not flash before the names on every visit.
  if (isLoadingRules || isLoadingMethods || isErrorRules) {
    return <SkeletonTable rowsCount={5} columnsCount={7} isError={isErrorRules} />;
  }

  return (
    <div className="mt-24">
      <Heading variant={Heading.TYPES.H3} className="pb-12">
        {TEXTS.TITLE}
      </Heading>
      <div className="flex justify-end pb-12">
        <Button
          color="primary"
          onClick={() => setIsRuleDialogOpen(true)}
          startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
        >
          {TEXTS.ADD_RULE}
        </Button>
      </div>
      <MoiAssignmentRulesTable
        organization={organization}
        rules={orderedRules}
        isProcessing={isProcessing}
        emptyStateText={TEXTS.EMPTY_STATE}
        moiMethodNameByKey={toMoiMethodNameByKey(methods)}
        contactNamesById={contactNamesById}
        onReorder={handleReorder}
        onEditRule={handleEditRuleClicked}
        onDeleteRule={handleDeleteRule}
        onEnabledChanged={handleEnabledChanged}
      />
      {isRuleDialogOpen ? (
        <AddOrEditMoiAssignmentRuleDialog
          onCancel={handleCloseRuleDialog}
          onSubmit={handleSubmitRule}
          rule={ruleToEdit}
          contactFullName={staticContactNameOf(ruleToEdit, contactNamesById)}
        />
      ) : null}
    </div>
  );
};

export default MoiAssignmentRulesTab;
